import { database, DB_PREFIX, cacheGet, cacheSet, CACHE_TTL, invalidateCache } from "./config";
import { ref, push, serverTimestamp, set, onValue, update, remove } from "firebase/database";
import { getBusinessReviews } from "./review";

export const getAllBusinesses = async () => {
  const cacheKey = 'businesses:all';
  const cached = cacheGet(cacheKey);
  if (cached !== undefined) return cached;

  if (!database) {
    console.warn("Firebase not initialized");
    return [];
  }
  try {
    const businessesRef = ref(database, `${DB_PREFIX}businesses`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(businessesRef, resolve, reject, { onlyOnce: true });
    });
    const businesses = [];
    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        businesses.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
    }
    cacheSet(cacheKey, businesses, CACHE_TTL.businesses);
    return businesses;
  } catch (error) {
    console.error("Error getting all businesses:", error);
    return [];
  }
};

export const addBusiness = async (businessData) => {
  if (!database) {
    console.warn("Firebase not initialized - business not saved");
    return null;
  }

  try {
    const businessesRef = ref(database, `${DB_PREFIX}businesses`);
    const newBusinessRef = push(businessesRef);
    await set(newBusinessRef, {
      ...businessData,
      createdAt: serverTimestamp(),
      reviewCount: 0,
      averageRating: 0,
      ghostedCount: 0 // Track ghosted reports
    });
    console.log("Business saved to Firebase successfully");
    invalidateCache('businesses:all');
    return newBusinessRef.key;

  } catch (error) {
    console.error("Error saving business to Firebase:", error);
    throw error;
  }
}

// Create a business for company reviews if it doesn't already exist
export const createBusinessIfNotExists = async (companyName, companyWebsite) => {
  if (!database || !companyName) {
    console.warn("Firebase not initialized or company name missing");
    return null;
  }

  try {
    // First check if business already exists
    const businessesRef = ref(database, `${DB_PREFIX}businesses`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(businessesRef, resolve, reject, { onlyOnce: true });
    });

    let existingBusiness = null;
    snapshot.forEach((childSnapshot) => {
      const business = childSnapshot.val();
      // Match by name (case insensitive)
      if (business.name && business.name.toLowerCase() === companyName.toLowerCase()) {
        existingBusiness = { id: childSnapshot.key, ...business };
      }
    });

    if (existingBusiness) {
      console.log("Business already exists:", existingBusiness.name);
      return existingBusiness.id;
    }

    // Create new business
    const newBusinessRef = push(businessesRef);
    await set(newBusinessRef, {
      name: companyName,
      website: companyWebsite || "",
      createdAt: serverTimestamp(),
      reviewCount: 0,
      averageRating: 0,
      ghostedCount: 0,
      createdByHiringManager: true
    });
    console.log("Business created for company reviews:", companyName);
    return newBusinessRef.key;
  } catch (error) {
    console.error("Error creating business:", error);
    return null;
  }
};

export const searchBusinesses = async (query) => {
  if (!database) {
    console.warn("Firebase not initialized");
    return [];
  }
  try {
    const businessesRef = ref(database, `${DB_PREFIX}businesses`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(businessesRef, resolve, reject, { onlyOnce: true });
    });
    const businesses = [];
    snapshot.forEach((childSnapshot) => {
      businesses.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });
    // Sort by creation date (newest first)
    businesses.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    return businesses;
  } catch (error) {
    console.error("Error searching businesses:", error);
    return [];
  }
}
export const updateBusiness = async (businessId, updates) => {
  if (!database) {
    console.warn("Firebase not initialized");
    return;
  }

  try {
    const businessRef = ref(database, `${DB_PREFIX}businesses/${businessId}`);
    await update(businessRef, updates);

    // Also update businessName on all reviews for this business if name changed
    if (updates.name) {
      const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
      const snapshot = await new Promise((resolve, reject) => {
        onValue(reviewsRef, resolve, reject, { onlyOnce: true });
      });
      const updatePromises = [];
      snapshot.forEach((childSnapshot) => {
        const review = childSnapshot.val();
        if (review.businessId === businessId) {
          updatePromises.push(
            update(ref(database, `${DB_PREFIX}reviews/${childSnapshot.key}`), { businessName: updates.name })
          );
        }
      });
      invalidateCache('businesses:all');
      await Promise.all(updatePromises);
    }
  } catch (error) {
    console.error("Error updating business:", error);
    throw error;
  }
}

export const updateBusinessStats = async (businessId) => {
  if (!database) return;

  try {
    const reviews = await getBusinessReviews(businessId);
    const reviewCount = reviews.length;
    const averageRating = reviewCount > 0
      ? reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviewCount
      : 0;

    // Count ghosted reports
    const ghostedCount = reviews.filter(review => review.followUp === 'ghosted').length;

    const businessRef = ref(database, `${DB_PREFIX}businesses/${businessId}`);
    const businessSnapshot = await new Promise((resolve, reject) => {
      onValue(businessRef, resolve, reject, { onlyOnce: true });
    });

    if (businessSnapshot.exists()) {
      await set(businessRef, {
        ...businessSnapshot.val(),
        reviewCount,
        averageRating,
        ghostedCount
      });
    }
  } catch (error) {
    console.error("Error updating business stats:", error);
  }
};

// Recalculate all business stats - fixes out-of-sync reviewCount values
export const recalculateAllBusinessStats = async () => {
  if (!database) {
    console.warn("Firebase not initialized");
    return { success: false, message: "Firebase not initialized" };
  }

  try {
    console.log("Starting recalculation of all business stats...");

    // Get all businesses
    const businesses = await getAllBusinesses();
    console.log(`Found ${businesses.length} businesses to recalculate`);

    let updated = 0;
    let errors = 0;
    const changes = [];

    for (const business of businesses) {
      try {
        // Get actual review count for this business
        const reviews = await getBusinessReviews(business.id);
        const actualReviewCount = reviews.length;
        const actualAverageRating = actualReviewCount > 0
          ? reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / actualReviewCount
          : 0;
        // Count ghosted reports from reviews
        const actualGhostedCount = reviews.filter(review => review.followUp === 'ghosted').length;

        const oldCount = business.reviewCount || 0;
        const oldRating = business.averageRating || 0;
        const oldGhostedCount = business.ghostedCount || 0;

        // Only update if there's a difference
        if (oldCount !== actualReviewCount || Math.abs(oldRating - actualAverageRating) > 0.01 || oldGhostedCount !== actualGhostedCount) {
          const businessRef = ref(database, `${DB_PREFIX}businesses/${business.id}`);
          await set(businessRef, {
            ...business,
            reviewCount: actualReviewCount,
            averageRating: actualAverageRating,
            ghostedCount: actualGhostedCount
          });

          changes.push({
            businessName: business.name,
            oldCount,
            newCount: actualReviewCount,
            oldRating: oldRating.toFixed(1),
            newRating: actualAverageRating.toFixed(1),
            oldGhostedCount,
            newGhostedCount: actualGhostedCount
          });
          updated++;
          console.log(`Updated ${business.name}: ${oldCount} -> ${actualReviewCount} reviews, ghosted: ${oldGhostedCount} -> ${actualGhostedCount}`);
        }
        invalidateCache('businesses:all');
      } catch (err) {
        console.error(`Error updating ${business.name}:`, err);
        errors++;
      }
    }

    const result = {
      success: true,
      message: `Recalculated ${businesses.length} businesses. Updated: ${updated}, Errors: ${errors}`,
      changes,
      totalBusinesses: businesses.length,
      updated,
      errors
    };

    console.log("Recalculation complete:", result);
    return result;
  } catch (error) {
    console.error("Error recalculating business stats:", error);
    return { success: false, message: error.message };
  }
};

export const getBusinessById = async (businessId) => {
  if (!database) {
    console.warn("Firebase not initialized");
    return null;
  }
  try {
    const businessRef = ref(database, `${DB_PREFIX}businesses/${businessId}`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(businessRef, resolve, reject, { onlyOnce: true });
    });
    if (snapshot.exists()) {
      return { id: snapshot.key, ...snapshot.val() };
    }
    return null;
  } catch (error) {
    console.error("Error getting business by ID:", error);
    return null;
  }
};


export const deleteBusiness = async (businessId) => {
  if (!database) {
    console.warn("Firebase not initialized - business not deleted");
    return false;
  }

  try {
    // First, delete all reviews associated with this business
    const reviews = await getBusinessReviews(businessId);
    for (const review of reviews) {
      const reviewRef = ref(database, `${DB_PREFIX}reviews/${review.id}`);
      await remove(reviewRef);
    }

    // Then delete the business itself
    const businessRef = ref(database, `${DB_PREFIX}businesses/${businessId}`);
    await remove(businessRef);

    console.log("Business and associated reviews deleted successfully");
    invalidateCache('businesses:all');
    return true;
  } catch (error) {
    console.error("Error deleting business:", error);
    throw error;
  }
};